import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Heading,
  VStack,
  HStack,
  Flex,
  Link,
  Divider,
  Center,
  useToast,
  PinInput,
  PinInputField,
  Progress
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaGoogle, FaEye, FaEyeSlash, FaMobile } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import authService from '../services/authService';

// Add global styles for autofill - with better mobile visibility
const globalStyles = `
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: inset 0 0 0px 1000px rgba(255, 255, 255, 0.15) !important;
    -webkit-text-fill-color: white !important;
    background-color: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    caret-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
  
  input:-webkit-autofill::first-line {
    color: white !important;
    font-size: inherit !important;
    font-family: inherit !important;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  if (!document.head.querySelector('[data-autofill-styles]')) {
    styleElement.setAttribute('data-autofill-styles', 'true');
    document.head.appendChild(styleElement);
  }
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const AuthPage = () => {
  const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'otp', 'verify-otp'
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const pinInputRef = useRef();

  // Timer effect for OTP
  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
    } else if (timer === 0 && authMode === 'verify-otp') {
      setIsResendDisabled(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timer, authMode]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toast helper with different styles for different error types
  const showToast = (title, description, status, errorCode = null) => {
    let toastConfig = {
      title,
      description,
      status,
      duration: 5000,
      isClosable: true,
      position: 'top'
    };

    // Customize toast based on error codes
    switch (errorCode) {
      case 429: // Rate limiting
        toastConfig.status = 'warning';
        toastConfig.duration = 7000;
        break;
      case 404: // Not found
        toastConfig.status = 'error';
        break;
      case 500: // Server error
        toastConfig.status = 'error';
        toastConfig.duration = 6000;
        break;
      default:
        break;
    }

    toast(toastConfig);
  };

  const handleGoogleAuth = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      try {
        setIsGoogleLoading(true);
        console.log('Google authorization code received:', codeResponse.code);
        
        // Send authorization code to backend
        const response = await authService.googleOAuth(codeResponse.code);
        
        // Store JWT tokens
        authService.setTokens(response.access, response.refresh);
        
        // Show success message
        showToast(
          "Success", 
          `Welcome ${response.first_name}! Successfully logged in with Google.`,
          "success"
        );
        
        // Navigate to home page
        navigate('/');
        
      } catch (error) {
        console.error('Google OAuth error:', error);
        
        // Handle different error types
        if (error.response) {
          const { status, data } = error.response;
          
          switch (status) {
            case 400:
              showToast("Error", 
                data.error || data.code?.[0] || "Invalid authorization code", 
                "error", status);
              break;
              
            case 429:
              showToast("Rate Limited", 
                "Too many authentication attempts. Please wait before trying again.", 
                "warning", status);
              break;
              
            case 500:
              showToast("Server Error", 
                "Something went wrong on our end. Please try again later.", 
                "error", status);
              break;
              
            default:
              showToast("Error", 
                data.error || "Google authentication failed", 
                "error");
          }
        } else {
          showToast("Error", 
            error.error || "Network error. Please check your connection and try again.", 
            "error");
        }
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth popup error:', errorResponse);
      showToast("Error", 
        "Google authentication was cancelled or failed. Please try again.", 
        "error");
      setIsGoogleLoading(false);
    },
  });

  const handleEmailAuth = async () => {
    // Validation based on auth mode
    if (authMode === 'otp') {
      if (!email) {
        showToast("Error", "Please enter your email address.", "error");
        return;
      }
    } else if (authMode === 'login') {
      if (!email || !password) {
        showToast("Error", "Please enter both email and password.", "error");
        return;
      }
    } else if (authMode === 'signup') {
      if (!email || !password || !fullName || !confirmPassword) {
        showToast("Error", "Please fill in all required fields.", "error");
        return;
      }
      if (password !== confirmPassword) {
        showToast("Error", "Passwords do not match.", "error");
        return;
      }
    }

    try {
      setIsLoading(true);
      
      if (authMode === 'otp') {
        // Handle OTP request
        const response = await authService.sendOTP(email);
        showToast("Success", "OTP sent to your email", "success");
        setAuthMode('verify-otp');
        setTimer(60); // Start 60 second timer
        setOtp(''); // Clear any existing OTP
        setOtpError(false);
        
        // Focus on first PIN input after a small delay
        setTimeout(() => {
          if (pinInputRef.current) {
            pinInputRef.current.focus();
          }
        }, 100);
        
      } else if (authMode === 'login') {
        // Handle login
        const response = await authService.login(email, password);
        authService.setTokens(response.access, response.refresh);
        showToast("Success", "Successfully logged in", "success");
        navigate('/');
        
      } else if (authMode === 'signup') {
        // Handle signup
        const response = await authService.signup(email, fullName, password);
        authService.setTokens(response.access, response.refresh);
        showToast("Success", "Account created successfully", "success");
        navigate('/');
      }
      
    } catch (error) {
      console.error('Auth error:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            const errorMessage = data.non_field_errors?.[0] || 
                                data.email?.[0] || 
                                data.password?.[0] ||
                                Object.values(data)[0]?.[0] ||
                                'Invalid credentials';
            showToast("Error", errorMessage, "error", status);
            break;
            
          case 404:
            if (authMode === 'otp') {
              const errorMessage = data.error || data.detail || "No user exists with this email address.";
              showToast("Error", errorMessage, "error", status);
            } else {
              showToast("Error", "User not found", "error", status);
            }
            break;
            
          case 429:
            showToast("Rate Limited", 
              "Too many attempts. Please wait before trying again.", 
              "warning", status);
            break;
            
          case 500:
            showToast("Server Error", 
              "Something went wrong on our end. Please try again later.", 
              "error", status);
            break;
            
          default:
            showToast("Error", "An unexpected error occurred", "error");
        }
      } else if (error.error) {
        // Handle error object from authService
        showToast("Error", error.error, "error");
      } else {
        showToast("Error", "Network error. Please check your connection.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      showToast("Error", "Please enter the complete 6-digit OTP", "error");
      return;
    }

    try {
      setIsLoading(true);
      setOtpError(false);
      
      const response = await authService.verifyOTP(email, otp);
      authService.setTokens(response.access, response.refresh);
      showToast("Success", "Successfully verified and logged in", "success");
      navigate('/');
      
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError(true);
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            const errorMessage = data.otp?.[0] || 
                                data.non_field_errors?.[0] || 
                                "Invalid or expired OTP";
            showToast("Error", errorMessage, "error", status);
            break;
            
          case 404:
            showToast("Error", "OTP session not found. Please request a new OTP.", "error", status);
            setAuthMode('otp');
            break;
            
          case 429:
            showToast("Rate Limited", 
              "Too many verification attempts. Please wait before trying again.", 
              "warning", status);
            break;
            
          case 500:
            showToast("Server Error", 
              "Something went wrong on our end. Please try again later.", 
              "error", status);
            break;
            
          default:
            showToast("Error", "An unexpected error occurred", "error");
        }
      } else {
        showToast("Error", "Network error. Please check your connection.", "error");
      }
      
      // Clear OTP on error for security
      setOtp('');
      
      // Focus back to first input
      setTimeout(() => {
        if (pinInputRef.current) {
          pinInputRef.current.focus();
        }
      }, 100);
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (isResendDisabled) return;
    
    try {
      setIsLoading(true);
      const response = await authService.sendOTP(email);
      showToast("Success", "OTP resent successfully", "success");
      setTimer(60); // Restart timer
      setOtp(''); // Clear current OTP
      setOtpError(false);
      
      // Focus on first PIN input
      setTimeout(() => {
        if (pinInputRef.current) {
          pinInputRef.current.focus();
        }
      }, 100);
      
    } catch (error) {
      const errorCode = error.status;
      if (errorCode === 429) {
        setIsResendDisabled(true);
        showToast("Rate Limited", error.error, "warning", 429);
      } else {
        showToast("Error", error.error || "Failed to resend OTP", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCardOpacity = () => {
    return focusedInput ? 0.98 : 0.85;
  };

  // Password strength calculator
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, text: '', color: '' };
    
    let score = 0;
    let feedback = [];
    
    // Length check
    if (password.length >= 8) score += 25;
    else feedback.push('At least 8 characters');
    
    // Uppercase check
    if (/[A-Z]/.test(password)) score += 25;
    else feedback.push('One uppercase letter');
    
    // Lowercase check
    if (/[a-z]/.test(password)) score += 25;
    else feedback.push('One lowercase letter');
    
    // Number or special character check
    if (/[\d\W]/.test(password)) score += 25;
    else feedback.push('One number or special character');
    
    // Determine strength level and neon colors
    let text, color, bgColor;
    if (score < 25) {
      text = 'Very Weak';
      color = '#ff0040'; // Neon red
      bgColor = 'rgba(255, 0, 64, 0.2)';
    } else if (score < 50) {
      text = 'Weak';
      color = '#ff8c00'; // Neon orange
      bgColor = 'rgba(255, 140, 0, 0.2)';
    } else if (score < 75) {
      text = 'Good';
      color = '#ffff00'; // Neon yellow
      bgColor = 'rgba(255, 255, 0, 0.2)';
    } else if (score < 100) {
      text = 'Strong';
      color = '#00ff80'; // Neon green
      bgColor = 'rgba(0, 255, 128, 0.2)';
    } else {
      text = 'Very Strong';
      color = '#00ffff'; // Neon cyan
      bgColor = 'rgba(0, 255, 255, 0.2)';
    }
    
    return { score, text, color, bgColor, feedback };
  };

  // Password strength meter component
  const PasswordStrengthMeter = ({ password }) => {
    const strength = getPasswordStrength(password);
    
    if (!password) return null;
    
    return (
      <Box mt={2} mb={4}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="sm" color="gray.300">
            Password Strength
          </Text>
          <Text 
            fontSize="sm" 
            color={strength.color}
            fontWeight="600"
            textShadow={`0 0 10px ${strength.color}`}
          >
            {strength.text}
          </Text>
        </Flex>
        
        <Box position="relative">
          <Progress
            value={strength.score}
            size="sm"
            bg="rgba(255, 255, 255, 0.1)"
            borderRadius="full"
            sx={{
              '& > div': {
                background: `linear-gradient(90deg, ${strength.color}40, ${strength.color})`,
                boxShadow: `0 0 15px ${strength.color}80`,
                transition: 'all 0.3s ease'
              }
            }}
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg={strength.bgColor}
            borderRadius="full"
            opacity="0.3"
            filter={`blur(8px)`}
            pointerEvents="none"
          />
        </Box>
        
        {strength.feedback.length > 0 && (
          <Text fontSize="xs" color="gray.400" mt={2}>
            Missing: {strength.feedback.join(', ')}
          </Text>
        )}
      </Box>
    );
  };

  const getInputStyles = () => ({
    bg: "rgba(255, 255, 255, 0.05)",
    color: "white",
    fontSize: ["18px", "18px", "16px"], // Bigger on mobile, normal on desktop
    fontWeight: "500",
    border: "1px solid",
    borderColor: "rgba(255, 255, 255, 0.3)",
    _placeholder: { 
      color: "rgba(255, 255, 255, 0.6)",
      fontSize: ["18px", "18px", "16px"]
    },
    _focus: {
      borderColor: "rgba(255, 255, 255, 0.9)",
      bg: "rgba(255, 255, 255, 0.08)",
      boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)",
      color: "white"
    },
    _hover: {
      borderColor: "rgba(255, 255, 255, 0.6)",
      bg: "rgba(255, 255, 255, 0.07)"
    },
    // Fix autofill styling with better mobile visibility
    _autofill: {
      bg: "rgba(255, 255, 255, 0.15) !important",
      color: "white !important",
      transition: "background-color 5000s ease-in-out 0s",
      boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.15) !important",
      WebkitTextFillColor: "white !important",
      caretColor: "white !important"
    },
    "&:-webkit-autofill": {
      bg: "rgba(255, 255, 255, 0.15) !important",
      color: "white !important",
      transition: "background-color 5000s ease-in-out 0s",
      boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.15) !important",
      WebkitTextFillColor: "white !important",
      caretColor: "white !important"
    },
    "&:-webkit-autofill:hover": {
      bg: "rgba(255, 255, 255, 0.18) !important",
      boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.18) !important",
      WebkitTextFillColor: "white !important",
      caretColor: "white !important"
    },
    "&:-webkit-autofill:focus": {
      bg: "rgba(255, 255, 255, 0.2) !important",
      boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.2) !important",
      WebkitTextFillColor: "white !important",
      caretColor: "white !important"
    },
    "&:-webkit-autofill:active": {
      bg: "rgba(255, 255, 255, 0.2) !important",
      boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.2) !important",
      WebkitTextFillColor: "white !important",
      caretColor: "white !important"
    }
  });

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      backgroundImage="url('/auth-bg.webp')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundAttachment="fixed"
      py={10}
      px={4}
    >
      <MotionBox
        p={8}
        maxWidth="450px"
        width="100%"
        borderWidth={1}
        borderRadius="lg"
        boxShadow="xl"
        bg={`rgba(25, 25, 25, ${getCardOpacity()})`}
        backdropFilter="blur(0px)"
        borderColor="rgba(25, 25, 25, 0.2)"
        color="white"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Heading as="h2" size="lg" textAlign="center" mb={6} color="white">
          {authMode === 'login' && 'Login'}
          {authMode === 'signup' && 'Sign Up'}
          {authMode === 'otp' && 'Login with OTP'}
          {authMode === 'verify-otp' && 'Enter OTP'}
        </Heading>

        {/* Google Auth Button - Show in all modes except verify-otp */}
        {authMode !== 'verify-otp' && (
          <MotionButton
            leftIcon={<FaGoogle />}
            bg="transparent"
            color="white"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.6)"
            width="full"
            mb={4}
            onClick={() => handleGoogleAuth()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            _hover={{ 
              bg: "rgba(255, 255, 255, 0.1)", 
              borderColor: "rgba(255, 255, 255, 0.8)"
            }}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            isDisabled={isLoading || isGoogleLoading}
            isLoading={isGoogleLoading}
            loadingText="Signing in with Google..."
          >
            Continue with Google
          </MotionButton>
        )}

        {/* OTP Login Button - Show only in login mode */}
        {authMode === 'login' && (
          <MotionButton
            leftIcon={<FaMobile />}
            bg="transparent"
            color="white"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.6)"
            width="full"
            mb={4}
            onClick={() => setAuthMode('otp')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            _hover={{ 
              bg: "rgba(255, 255, 255, 0.1)", 
              borderColor: "rgba(255, 255, 255, 0.8)"
            }}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            isDisabled={isLoading}
          >
            Login Using OTP
          </MotionButton>
        )}

        {/* Divider - Show except in verify-otp mode */}
        {authMode !== 'verify-otp' && (
          <Center my={4}>
            <Divider orientation="horizontal" flex="1" borderColor="rgba(255, 255, 255, 0.3)" />
            <Text px={2} color="gray.400">OR</Text>
            <Divider orientation="horizontal" flex="1" borderColor="rgba(255, 255, 255, 0.3)" />
          </Center>
        )}

        {/* OTP Input Section - Show in verify-otp mode */}
        {authMode === 'verify-otp' && (
          <>
            <Text textAlign="center" mb={4} color="gray.300">
              Enter the 6-digit code sent to your email
            </Text>
            <Text textAlign="center" mb={6} color="gray.400" fontSize="sm">
              {email}
            </Text>
            
            <VStack spacing={4} mb={6}>
              <HStack spacing={3}>
                <PinInput
                  otp
                  size="lg"
                  focusBorderColor="rgba(255, 255, 255, 0.8)"
                  onChange={(value) => setOtp(value)}
                  value={otp}
                  isInvalid={otpError}
                  ref={pinInputRef}
                >
                  {[...Array(6)].map((_, index) => (
                    <PinInputField
                      key={index}
                      bg="rgba(255, 255, 255, 0.05)"
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.3)"
                      color="white"
                      fontSize={["22px", "22px", "20px"]} // Bigger on mobile
                      fontWeight="600"
                      textAlign="center"
                      _focus={{
                        borderColor: "rgba(255, 255, 255, 0.9)",
                        bg: "rgba(255, 255, 255, 0.08)",
                        boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)"
                      }}
                      _invalid={{
                        borderColor: "#ff4757",
                        boxShadow: "0 0 0 2px rgba(255, 71, 87, 0.3)"
                      }}
                      _hover={{
                        borderColor: "rgba(255, 255, 255, 0.6)"
                      }}
                      // Fix autofill for PIN inputs too
                      sx={{
                        "&:-webkit-autofill": {
                          bg: "rgba(255, 255, 255, 0.15) !important",
                          boxShadow: "inset 0 0 0px 1000px rgba(255, 255, 255, 0.15) !important",
                          WebkitTextFillColor: "white !important",
                          caretColor: "white !important"
                        }
                      }}
                    />
                  ))}
                </PinInput>
              </HStack>
              
              {timer > 0 ? (
                <Text color="gray.400" fontSize="sm">
                  Resend OTP in {formatTime(timer)}
                </Text>
              ) : (
                <Button
                  variant="link"
                  color="white"
                  onClick={handleResendOTP}
                  isDisabled={isLoading}
                  fontSize="sm"
                  _hover={{ color: "gray.300" }}
                >
                  Resend OTP
                </Button>
              )}
            </VStack>

            <MotionButton
              onClick={handleVerifyOTP}
              bg="black"
              color="white"
              width="full"
              mb={4}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              _hover={{ bg: "gray.800" }}
              isLoading={isLoading}
              loadingText="Verifying..."
              isDisabled={otp.length !== 6}
            >
              Verify OTP
            </MotionButton>

            <Button
              variant="link"
              color="gray.400"
              onClick={() => {
                setAuthMode('login');
                setOtp('');
                setTimer(0);
              }}
              fontSize="sm"
              _hover={{ color: "white" }}
            >
              Back to Login
            </Button>
          </>
        )}

        {/* Email Form - Show in login, signup, and otp modes */}
        {(authMode === 'login' || authMode === 'signup' || authMode === 'otp') && (
          <>
            <FormControl isRequired mb={4}>
              <FormLabel color="gray.300" fontSize={["16px", "16px", "14px"]}>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                {...getInputStyles()}
                autoComplete="email"
                isDisabled={isLoading}
              />
            </FormControl>

            {/* Show full name field only in signup mode */}
            {authMode === 'signup' && (
              <FormControl isRequired mb={4}>
                <FormLabel color="gray.300" fontSize={["16px", "16px", "14px"]}>Full Name</FormLabel>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  {...getInputStyles()}
                  autoComplete="name"
                  isDisabled={isLoading}
                />
              </FormControl>
            )}

            {/* Password field - Show only in login and signup modes */}
            {(authMode === 'login' || authMode === 'signup') && (
              <FormControl isRequired mb={authMode === 'signup' ? 2 : 4}>
                <FormLabel color="gray.300" fontSize={["16px", "16px", "14px"]}>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    {...getInputStyles()}
                    autoComplete={authMode === 'login' ? "current-password" : "new-password"}
                    isDisabled={isLoading}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                      size="sm"
                      color="gray.400"
                      _hover={{ color: "white" }}
                    />
                  </InputRightElement>
                </InputGroup>
                
                {/* Password Strength Meter for signup */}
                {authMode === 'signup' && (
                  <PasswordStrengthMeter password={password} />
                )}
              </FormControl>
            )}

            {/* Confirm password field - Show only in signup mode */}
            {authMode === 'signup' && (
              <FormControl isRequired mb={4}>
                <FormLabel color="gray.300" fontSize={["16px", "16px", "14px"]}>Confirm Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  {...getInputStyles()}
                  autoComplete="new-password"
                  isDisabled={isLoading}
                />
              </FormControl>
            )}

            <MotionButton
              onClick={handleEmailAuth}
              bg="black"
              color="white"
              width="full"
              mt={4}
              mb={4}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              _hover={{ bg: "gray.800" }}
              isLoading={isLoading}
              loadingText={authMode === 'otp' ? 'Sending OTP...' : (authMode === 'login' ? 'Logging in...' : 'Signing up...')}
            >
              {authMode === 'otp' ? 'Send OTP' : (authMode === 'login' ? 'Login' : 'Sign Up')}
            </MotionButton>

            {/* Mode switching links */}
            <Text textAlign="center" color="gray.400">
              {authMode === 'login' && (
                <>
                  Don't have an account?{' '}
                  <Link color="white" onClick={() => setAuthMode('signup')} _hover={{ color: "gray.300" }}>
                    Sign Up
                  </Link>
                </>
              )}
              {authMode === 'signup' && (
                <>
                  Already have an account?{' '}
                  <Link color="white" onClick={() => setAuthMode('login')} _hover={{ color: "gray.300" }}>
                    Login
                  </Link>
                </>
              )}
              {authMode === 'otp' && (
                <>
                  Want to use password?{' '}
                  <Link color="white" onClick={() => setAuthMode('login')} _hover={{ color: "gray.300" }}>
                    Login with Password
                  </Link>
                </>
              )}
            </Text>
          </>
        )}
      </MotionBox>
    </Flex>
  );
};

export default AuthPage;
